import ConversationApi from 'dashboard/api/inbox/conversation';
import types from 'dashboard/store/mutation-types';
import {
  beginConversationPositionRestore,
  getConversationRouteId,
} from 'dashboard/store/modules/conversations/helpers/historyPosition';
import {
  captureConversationAccess,
  suspendConversationFrames,
  resumeConversationFrames,
  revokeConversationAccess,
} from './conversationReads';
import {
  clearAccess,
  revalidateAccess,
  reloadVisibleCollections,
} from './accessActions';

// Preserve only the active composition. All other access-dependent caches use
// the same invalidation as a global change, including reads already in flight.
export const revalidateOnReconnect = async (context, { isConnected }) => {
  const { rootState, dispatch, commit } = context;
  const route = rootState.route;
  const selectedId = getConversationRouteId(context);
  if (!selectedId || rootState.conversations.selectedChatId !== selectedId)
    return revalidateAccess(context);

  beginConversationPositionRestore(context);
  clearAccess(context, {
    preserveReadingPosition: true,
    preserveComposer: true,
  });
  suspendConversationFrames(rootState);
  const ownerIsCurrent = captureConversationAccess(context);
  const accessIsCurrent = captureConversationAccess(context, selectedId);
  const isCurrent = () => ownerIsCurrent() && isConnected();
  try {
    const refreshed = await dispatch(
      'validityCheck',
      { preserveUISettings: true, coalesce: true },
      { root: true }
    );
    if (!refreshed || !isCurrent()) return null;
    // A conversation can be revoked offline without changing the global
    // revision. Only a direct authorized read can confirm the composition.
    let conversation;
    try {
      ({ data: conversation } = await ConversationApi.show(selectedId));
    } catch (error) {
      if (!isCurrent() || ![401, 403, 404].includes(error?.response?.status))
        throw error;
      revokeConversationAccess(rootState, selectedId, 'show');
      commit(types.REVOKE_CONVERSATION_ACCESS, selectedId, { root: true });
      resumeConversationFrames(rootState, isCurrent);
      return (await reloadVisibleCollections(context, isCurrent))
        ? isCurrent
        : null;
    }
    if (!isCurrent() || !accessIsCurrent()) return null;
    if (rootState.route !== route) return revalidateAccess(context);
    commit(types.CONFIRM_RECONNECTED_CONVERSATION, conversation, {
      root: true,
    });
    commit(`contacts/${types.SET_CONTACT_ITEM}`, conversation.meta.sender, {
      root: true,
    });
    await dispatch(
      'conversationLabels/setConversationLabel',
      {
        id: selectedId,
        data: conversation.labels,
      },
      { root: true }
    );
    if (!isCurrent() || !accessIsCurrent()) return null;
    resumeConversationFrames(rootState, isCurrent);
    if (!(await reloadVisibleCollections(context, isCurrent))) return null;
    if (!isCurrent() || !accessIsCurrent()) return null;
    if (rootState.route !== route) return revalidateAccess(context);
    // Frames blocked during the authorization read can be newer than its
    // snapshot. Recover forward pages after resuming, using the existing
    // history cursor/fences so concurrent frames cannot create a gap.
    const activeConversation = () =>
      rootState.conversations.allConversations.find(
        item => item.id === selectedId
      );
    let recovered = true;
    while (
      isCurrent() &&
      accessIsCurrent() &&
      activeConversation()?.historyHasMoreAfter
    ) {
      if (rootState.route !== route) return revalidateAccess(context);
      recovered = Boolean(
        // Each page supplies the next cursor; these reads must be sequential.
        // eslint-disable-next-line no-await-in-loop
        await dispatch('fetchNewerMessages', selectedId, { root: true })
      );
      if (!recovered) break;
    }
    if (!isCurrent() || !accessIsCurrent()) return null;
    if (rootState.route !== route) return revalidateAccess(context);
    const loaded = recovered
      ? await dispatch(
          'setActiveChat',
          { data: activeConversation() },
          { root: true }
        )
      : null;
    if (!isCurrent() || !accessIsCurrent()) return null;
    if (rootState.route !== route) return revalidateAccess(context);
    await Promise.all([
      dispatch('fetchAllAttachments', selectedId, { root: true }),
      loaded?.()
        ? dispatch('markMessagesRead', { id: selectedId }, { root: true })
        : Promise.resolve(),
    ]);
    if (!isCurrent() || !accessIsCurrent()) return null;
    if (rootState.route !== route) return revalidateAccess(context);
    commit(types.FINISH_COMPOSER_REVALIDATION, undefined, { root: true });
    return isCurrent;
  } catch (error) {
    return null;
  } finally {
    // A superseding global validation/session owns its own cleanup. A failed
    // current confirmation must never leave cached content looking authorized.
    if (ownerIsCurrent() && rootState.conversations.composerRevalidating) {
      clearAccess(context);
      suspendConversationFrames(rootState);
    }
  }
};
